import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Download, ExternalLink, FileText, Loader2, Lock, PenLine } from "lucide-react";
import InvestorShell from "./InvestorShell";
import { api, downloadBlob } from "./api";

function SignForm({ docId, onSigned }) {
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const sign = async () => {
    setBusy(true);
    try {
      await api.post(`/investor/documents/${docId}/sign`, { typed_name: name, consent });
      toast.success("Document signed");
      onSigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <p className="text-xs font-bold text-offwhite">Signature required</p>
      <p className="mt-1 text-[11px] leading-relaxed text-muted">Type your full legal name to sign this document electronically.</p>
      <input className="field mt-3 !py-2.5 !text-sm" placeholder="Full legal name" value={name} onChange={(e) => setName(e.target.value)} data-testid="sign-typed-name" />
      <label className="mt-3 flex cursor-pointer items-start gap-2.5 text-[11px] leading-relaxed text-muted">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-red-700" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="sign-consent" />
        I agree to sign this document electronically and understand my typed name is my legal signature (ESIGN Act).
      </label>
      <button onClick={sign} disabled={name.trim().length < 2 || !consent || busy}
        className="mt-3 w-full rounded-full bg-offwhite px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-ink hover:bg-black disabled:opacity-40"
        data-testid="sign-submit-btn">
        {busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Sign Document"}
      </button>
    </div>
  );
}

export default function DataRoom() {
  const [groups, setGroups] = useState(null);
  const [gates, setGates] = useState(null);
  const [open, setOpen] = useState(null);
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/investor/documents").then(({ data }) => setGroups(data.categories)).catch((e) => {
      toast.error(e?.response?.data?.detail || "Data room unavailable");
      setGroups([]);
    });
    api.get("/investor/me").then(({ data }) => setGates(data.gates)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDoc = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/investor/documents/${id}`);
      setDoc(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Document unavailable"); }
    finally { setBusy(false); }
  };

  return (
    <InvestorShell gates={gates} title="Due Diligence Data Room" subtitle="Versioned documents · every view is access-logged">
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]" data-testid="dataroom-page">
        <div className="space-y-3">
          {!groups && <p className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>}
          {groups?.map((g) => (
            <div key={g.category} className="card-line overflow-hidden rounded-xl bg-white" data-testid={`dataroom-cat-${g.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <button onClick={() => setOpen(open === g.category ? null : g.category)}
                className="flex w-full items-center justify-between px-5 py-4 text-left"
                data-testid={`dataroom-toggle-${g.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="font-display text-sm font-bold text-offwhite">{g.category}
                  <span className="ml-2 font-mono text-[10px] text-muted">{g.documents.length}</span>
                </span>
                <ChevronDown size={15} className={`text-muted transition-transform ${open === g.category ? "rotate-180" : ""}`} />
              </button>
              {open === g.category && (
                <div className="border-t border-black/[0.06] px-5 py-3">
                  {!g.documents.length && <p className="py-2 text-xs text-muted">No documents published in this category yet.</p>}
                  {g.documents.map((d) => (
                    <button key={d.document_id} onClick={() => openDoc(d.document_id)} disabled={busy}
                      className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-surface/60"
                      data-testid={`doc-open-${d.document_id}`}>
                      <FileText size={14} className="shrink-0 text-red-700" />
                      <span className="min-w-0 flex-1 truncate text-xs font-semibold text-offwhite">{d.title}</span>
                      {d.visibility === "approved" && <Lock size={11} className="text-yellow-600" aria-label="Approved investors only" />}
                      <span className="font-mono text-[10px] text-muted">v{d.version} · {new Date(d.updated_at).toLocaleDateString()}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div>
          <div className="card-line sticky top-28 rounded-xl bg-white p-6" data-testid="doc-viewer">
            {!doc && <p className="text-sm text-muted">Select a document to view it here. Opening a document records an access-log entry.</p>}
            {doc && (
              <>
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-display text-base font-extrabold text-offwhite" data-testid="doc-title">{doc.title}</h2>
                  <span className="shrink-0 font-mono text-[10px] text-muted">v{doc.version}</span>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted">{doc.category} · updated {new Date(doc.updated_at).toLocaleString()}</p>
                <div className="prose-sm mt-4 whitespace-pre-wrap text-sm leading-relaxed text-muted" data-testid="doc-body">{doc.body || "Document content will be attached by Xtreme Exotics."}</div>
                {doc.url && (
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" className="btn-ghost mt-4 w-full !py-2 !text-xs" data-testid="doc-external-link">
                    <ExternalLink size={13} /> Open attached file
                  </a>
                )}
                {doc.file_path && (
                  <button onClick={async () => { try { await downloadBlob(`/investor/documents/${doc.document_id}/file`); } catch { toast.error("Download failed"); } }}
                    className="mt-3 w-full rounded-full bg-red-700 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-red-600"
                    data-testid="doc-download-btn">
                    <Download size={13} className="mr-1 inline" /> Download document file
                  </button>
                )}
                {doc.requires_signature && (
                  <div className="mt-4 rounded-xl border border-black/[0.08] bg-surface/50 p-4" data-testid="doc-sign-panel">
                    {doc.my_signature ? (
                      <p className="flex items-center gap-2 text-xs font-semibold text-emerald-700" data-testid="doc-signed-badge">
                        <PenLine size={14} /> Signed as "{doc.my_signature.typed_name}" on {new Date(doc.my_signature.signed_at).toLocaleString()} (v{doc.my_signature.doc_version})
                      </p>
                    ) : (
                      <SignForm docId={doc.document_id} onSigned={() => openDoc(doc.document_id)} />
                    )}
                  </div>
                )}
                <div className="mt-4 space-y-1 border-t border-black/[0.06] pt-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted">Version history</p>
                  {(doc.versions || []).map((v) => (
                    <p key={v.version} className="text-[11px] text-muted">v{v.version} — {new Date(v.at).toLocaleString()} by {v.by}</p>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </InvestorShell>
  );
}
